import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';

/**
 * Dashboard brief task 1 ("Hide Test Mode"): a tenant-level way to hide the Test/Live switch and
 * its banner entirely for one tenant's staff. Backed by the ASSUMED `GET /dashboard/settings`
 * route (see `api.ts`'s `settings` client and `lib/types.ts`'s `TenantSettings` doc comment for
 * exactly what's assumed and why). The refund policy is a SEPARATE concern with its own
 * reconciled route (`api.refundPolicy`, see `features/settings/RefundPolicyPage.tsx`) - it used
 * to live on this same `TenantSettings` shape before that reconciliation and has been split out.
 *
 * Same "courtesy, not the real gate" shape as `lib/clientPackage.tsx` - this ONLY decides whether
 * `components/ModeSwitch.tsx`'s `ModeSwitch`/`TestModeBanner` render for this tenant's staff. It
 * is not a security control (nothing about hiding the switch stops a request being made in either
 * mode), so it fails open to "show it" (`hideModeSwitch: false`) while loading/on error, rather
 * than flashing it hidden then visible once the real value arrives.
 */
interface TenantSettingsState {
  hideModeSwitch: boolean;
  isLoading: boolean;
}

const TenantSettingsContext = createContext<TenantSettingsState>({
  hideModeSwitch: false,
  isLoading: true,
});

export function TenantSettingsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const settingsQuery = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: api.settings.get,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });

  const value = useMemo<TenantSettingsState>(
    () => ({
      hideModeSwitch: settingsQuery.data?.hideModeSwitch ?? false,
      isLoading: settingsQuery.isLoading,
    }),
    [settingsQuery.data, settingsQuery.isLoading],
  );

  return <TenantSettingsContext.Provider value={value}>{children}</TenantSettingsContext.Provider>;
}

export function useTenantSettings(): TenantSettingsState {
  return useContext(TenantSettingsContext);
}
